import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { getFirestoreInstance } from './firestore';
import { planGigDuplicate, planGigReschedule } from '@gigdriver/domain';
import type { Gig, Call, Position, Booking, Quote, Item, DuplicatePriceMode } from '@gigdriver/domain';
import { fromFirestore } from './serde';
import { getClient } from './clients';
import { callable } from './functions';

// Gig operations
export async function createGig(
  orgId: string,
  gig: Omit<Gig, 'id' | 'createdAt' | 'updatedAt' | 'calls' | 'clientName'>,
): Promise<string> {
  // Every gig needs a main venue (a Venue document id); its time zone is what
  // the gig's date and call times are shown in.
  if (!gig.venueId?.trim()) throw new Error('A venue is required');
  const gigsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs');
  const gigRef = doc(gigsRef);
  const gigId = gigRef.id;
  const client = gig.clientId ? await getClient(orgId, gig.clientId) : null;

  await setDoc(gigRef, {
    ...gig,
    id: gigId,
    clientName: client?.name,
    calls: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return gigId;
}

export async function getGig(orgId: string, gigId: string): Promise<Gig | null> {
  const gigRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId);
  const docSnap = await getDoc(gigRef);
  return docSnap.exists() ? fromFirestore<Gig>(docSnap.data()) : null;
}

export async function updateGig(orgId: string, gigId: string, updates: Partial<Gig>): Promise<void> {
  const gigRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId);
  // Re-denormalize clientName whenever clientId changes, so a reassigned
  // client doesn't leave the old name stuck on the gig.
  const clientName = updates.clientId ? (await getClient(orgId, updates.clientId))?.name : undefined;
  await updateDoc(gigRef, {
    ...updates,
    ...(updates.clientId ? { clientName } : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function getOrgGigs(orgId: string): Promise<Gig[]> {
  const gigsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs');
  const snapshot = await getDocs(gigsRef);
  return snapshot.docs.map(doc => fromFirestore<Gig>(doc.data()));
}

export async function deleteGig(orgId: string, gigId: string): Promise<void> {
  const gigRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId);
  await deleteDoc(gigRef);
}

// Everything a duplicate needs from the source gig. Exposed separately so the
// Duplicate dialog can preview (counts, catalog price changes) before writing.
export interface GigDuplicateSource {
  gig: Gig;
  calls: Call[];
  positions: Position[];
  // The source's active quote, or null. Read here rather than through
  // quotes.ts, which already imports this module.
  quote: Quote | null;
}

export async function getGigDuplicateSource(orgId: string, gigId: string): Promise<GigDuplicateSource> {
  const gig = await getGig(orgId, gigId);
  if (!gig) throw new Error('Gig to duplicate was not found');
  const [calls, positions, quoteSnap] = await Promise.all([
    getGigCalls(orgId, gigId),
    getGigPositions(orgId, gigId),
    gig.activeQuoteId
      ? getDoc(doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'quotes', gig.activeQuoteId))
      : Promise.resolve(null),
  ]);
  const quote = quoteSnap?.exists() ? fromFirestore<Quote>(quoteSnap.data()) : null;
  return { gig, calls, positions, quote };
}

export interface DuplicateGigOptions {
  // YYYY-MM-DD, interpreted in the gig's venue zone.
  newDate: string;
  name?: string;
  // Fallback zone, and the zone of a venue by id (from the loaded venues).
  timeZone: string;
  timeZoneFor?: (venueId: string | undefined) => string | undefined;
  priceMode: DuplicatePriceMode;
  // Current item catalog, used only when priceMode is 'current'.
  catalog: Item[];
}

// Firestore caps a batch at 500 writes.
const MAX_BATCH_WRITES = 500;

// Copies a gig (calls, positions, and the active quote's non-labor lines —
// see planGigDuplicate) onto a new date in ONE atomic batch, so a failure
// never leaves a half-copied gig. Doc refs are generated up front so the
// callIds remap and Gig.calls / activeQuoteId are written with the docs
// instead of through createCall's per-call read-modify-write.
export async function duplicateGig(orgId: string, sourceGigId: string, options: DuplicateGigOptions): Promise<string> {
  const db = getFirestoreInstance();
  const source = await getGigDuplicateSource(orgId, sourceGigId);

  const gigsRef = collection(db, 'organizations', orgId, 'gigs');
  const newGigRef = doc(gigsRef);
  const newGigId = newGigRef.id;
  const callsRef = collection(db, 'organizations', orgId, 'gigs', newGigId, 'calls');

  // Re-denormalize clientName like createGig does, rather than trusting the
  // source's copy (the client may have been renamed since).
  const client = source.gig.clientId ? await getClient(orgId, source.gig.clientId) : null;

  const plan = planGigDuplicate({
    orgId,
    gig: { ...source.gig, clientName: client?.name ?? source.gig.clientName },
    calls: source.calls,
    positions: source.positions,
    quote: source.quote,
    catalog: options.catalog,
    priceMode: options.priceMode,
    newDate: options.newDate,
    name: options.name,
    timeZone: options.timeZone,
    timeZoneFor: options.timeZoneFor,
    newGigId,
    newId: () => doc(callsRef).id,
  });

  const writeCount = 1 + plan.calls.length + plan.positions.length + (plan.quote ? 1 : 0);
  if (writeCount > MAX_BATCH_WRITES) {
    throw new Error(`This gig is too large to duplicate in one step (${writeCount} records)`);
  }

  const batch = writeBatch(db);
  const stamps = () => ({ createdAt: serverTimestamp(), updatedAt: serverTimestamp() });

  let quoteId: string | undefined;
  if (plan.quote) {
    const quoteRef = doc(collection(db, 'organizations', orgId, 'gigs', newGigId, 'quotes'));
    quoteId = quoteRef.id;
    batch.set(quoteRef, {
      ...plan.quote,
      id: quoteId,
      orgId,
      gigId: newGigId,
      state: 'DRAFT',
      ...stamps(),
    });
  }

  batch.set(newGigRef, { ...plan.gig, ...(quoteId ? { activeQuoteId: quoteId } : {}), ...stamps() });
  plan.calls.forEach(c => {
    batch.set(doc(db, 'organizations', orgId, 'gigs', newGigId, 'calls', c.id), { ...c, ...stamps() });
  });
  plan.positions.forEach(p => {
    batch.set(doc(db, 'organizations', orgId, 'gigs', newGigId, 'positions', p.id), { ...p, ...stamps() });
  });

  await batch.commit();
  return newGigId;
}

// Moves a gig to a new date and shifts every call (and every position's
// scopeDate) by the same number of calendar days, in ONE atomic batch. The
// onCallUpdated / onPositionUpdated triggers then re-send updated calendar
// invites to accepted attendees and refresh booking snapshots.
export async function rescheduleGig(
  orgId: string,
  gigId: string,
  newDate: string,
  // Zone of a venue by id (from the loaded venues), and the fallback zone.
  timeZoneFor: (venueId: string | undefined) => string | undefined,
  timeZone: string,
): Promise<{ dayOffset: number }> {
  const db = getFirestoreInstance();
  const gig = await getGig(orgId, gigId);
  if (!gig) throw new Error('Gig not found');
  const [calls, positions] = await Promise.all([getGigCalls(orgId, gigId), getGigPositions(orgId, gigId)]);
  const plan = planGigReschedule({ gig, calls, positions, newDate, timeZoneFor, timeZone });

  const writeCount = 1 + plan.calls.length + plan.positions.length;
  if (writeCount > MAX_BATCH_WRITES) {
    throw new Error(`This gig is too large to reschedule in one step (${writeCount} records)`);
  }
  const batch = writeBatch(db);
  const stamp = { updatedAt: serverTimestamp() };
  batch.update(doc(db, 'organizations', orgId, 'gigs', gigId), { date: plan.gigDate, ...stamp });
  plan.calls.forEach(c => {
    batch.update(doc(db, 'organizations', orgId, 'gigs', gigId, 'calls', c.id), {
      startTime: c.startTime,
      endTime: c.endTime,
      ...stamp,
    });
  });
  plan.positions.forEach(p => {
    batch.update(doc(db, 'organizations', orgId, 'gigs', gigId, 'positions', p.id), { scopeDate: p.scopeDate, ...stamp });
  });
  await batch.commit();
  return { dayOffset: plan.dayOffset };
}

// Call operations
export async function createCall(
  orgId: string,
  gigId: string,
  call: Omit<Call, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const callsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'calls');
  const callRef = doc(callsRef);
  const callId = callRef.id;

  await setDoc(callRef, {
    ...call,
    id: callId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Add callId to gig's calls array
  const gig = await getGig(orgId, gigId);
  if (gig) {
    await updateGig(orgId, gigId, {
      calls: [...(gig.calls || []), callId],
    });
  }

  return callId;
}

export async function getCall(orgId: string, gigId: string, callId: string): Promise<Call | null> {
  const callRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'calls', callId);
  const docSnap = await getDoc(callRef);
  return docSnap.exists() ? fromFirestore<Call>(docSnap.data()) : null;
}

export async function updateCall(
  orgId: string,
  gigId: string,
  callId: string,
  updates: Partial<Call>,
): Promise<void> {
  const callRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'calls', callId);
  await updateDoc(callRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function getGigCalls(orgId: string, gigId: string): Promise<Call[]> {
  const callsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'calls');
  const snapshot = await getDocs(callsRef);
  return snapshot.docs.map(doc => fromFirestore<Call>(doc.data()));
}

// Deleting a call UNLINKS it from every Position that references it (and
// prunes it from any Booking under those positions whose own callIds
// includes it too), rather than cascade-deleting bookings outright —
// Positions/Bookings are no longer children of a Call, so a person keeps
// their other calls; they're only fully call-less if every one of their
// calls was removed. All in one atomic batch.
export async function deleteCall(orgId: string, gigId: string, callId: string): Promise<void> {
  const db = getFirestoreInstance();

  const positions = await getGigPositions(orgId, gigId);
  const affectedPositions = positions.filter(p => p.callIds.includes(callId));
  const bookingSnaps = await Promise.all(
    affectedPositions.map(pos =>
      getDocs(collection(db, 'organizations', orgId, 'gigs', gigId, 'positions', pos.id, 'bookings')),
    ),
  );

  const batch = writeBatch(db);
  affectedPositions.forEach((pos, i) => {
    batch.update(doc(db, 'organizations', orgId, 'gigs', gigId, 'positions', pos.id), {
      callIds: pos.callIds.filter(id => id !== callId),
      updatedAt: serverTimestamp(),
    });
    bookingSnaps[i].docs.forEach(d => {
      const booking = fromFirestore<Booking>(d.data());
      if (!booking.callIds.includes(callId)) return;
      batch.update(d.ref, {
        callIds: booking.callIds.filter(id => id !== callId),
        updatedAt: serverTimestamp(),
      });
    });
  });
  batch.delete(doc(db, 'organizations', orgId, 'gigs', gigId, 'calls', callId));
  await batch.commit();

  // Remove from gig's calls array
  const gig = await getGig(orgId, gigId);
  if (gig) {
    await updateGig(orgId, gigId, {
      calls: gig.calls?.filter(id => id !== callId) || [],
    });
  }
}

// Position operations — a Position lives at the gig level (a sibling of
// `calls`, not a child of one), holding an explicit `callIds` list rather
// than being duplicated per call.
export async function createPosition(
  orgId: string,
  gigId: string,
  position: Omit<Position, 'id' | 'createdAt' | 'updatedAt' | 'bookings'>,
): Promise<string> {
  const positionsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'positions');
  const posRef = doc(positionsRef);
  const posId = posRef.id;

  await setDoc(posRef, {
    ...position,
    id: posId,
    bookings: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return posId;
}

export async function getPosition(orgId: string, gigId: string, posId: string): Promise<Position | null> {
  const posRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'positions', posId);
  const docSnap = await getDoc(posRef);
  return docSnap.exists() ? fromFirestore<Position>(docSnap.data()) : null;
}

export async function updatePosition(
  orgId: string,
  gigId: string,
  posId: string,
  updates: Partial<Position>,
): Promise<void> {
  const posRef = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'positions', posId);
  await updateDoc(posRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function getGigPositions(orgId: string, gigId: string): Promise<Position[]> {
  const positionsRef = collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'positions');
  const snapshot = await getDocs(positionsRef);
  return snapshot.docs.map(doc => fromFirestore<Position>(doc.data()));
}

// Updates a Position's callIds, and cascade-prunes any now-out-of-scope
// call from every Booking under it (never deletes the booking) — "calls
// fall within the scope of the booking, and vice versa," enforced by
// pruning rather than blocking the edit.
export async function setPositionCallIds(
  orgId: string,
  gigId: string,
  posId: string,
  callIds: string[],
): Promise<void> {
  const db = getFirestoreInstance();
  const posRef = doc(db, 'organizations', orgId, 'gigs', gigId, 'positions', posId);
  const bookingsRef = collection(db, 'organizations', orgId, 'gigs', gigId, 'positions', posId, 'bookings');
  const bookingsSnap = await getDocs(bookingsRef);

  const batch = writeBatch(db);
  batch.update(posRef, { callIds, updatedAt: serverTimestamp() });
  bookingsSnap.docs.forEach(d => {
    const booking = fromFirestore<Booking>(d.data());
    const pruned = booking.callIds.filter(id => callIds.includes(id));
    if (pruned.length === booking.callIds.length) return;
    batch.update(d.ref, { callIds: pruned, updatedAt: serverTimestamp() });
  });
  await batch.commit();
}

// Refuses to delete a position that still has an ACCEPTED/BOOKED person on
// it — the coordinator must cancel each of them individually first (see
// cancelBooking in packages/client/src/bookings.ts). This is deliberate: a
// cancellation (with its own reason/fee) needs to stay visible on THIS gig's
// Staffing Budget forever, which only holds if the position it lives under
// still exists. Auto-cancelling everyone as part of the delete would be
// faster, but every one of those cancellations would immediately become an
// orphaned record — invisible on this gig, only findable via the person's
// own history — which defeats the reason this fee-tracking exists at all.
//
// DRAFT/OFFERED/DECLINED bookings still cascade-delete (no history worth
// keeping — mirrors deleteCall's own reasoning for the same tiers). Any
// booking already CANCELLED is left in place, not deleted — it IS the
// record — even though it will end up orphaned once the position doc below
// it is gone (still reachable via My Offers and the person's own history,
// just no longer visible on this gig).
export async function deletePosition(orgId: string, gigId: string, posId: string): Promise<void> {
  const db = getFirestoreInstance();
  const base = ['organizations', orgId, 'gigs', gigId, 'positions', posId] as const;

  const bookingsSnap = await getDocs(collection(db, ...base, 'bookings'));
  const bookings = bookingsSnap.docs.map(d => fromFirestore<Booking>(d.data()));

  const active = bookings.filter(b => b.state === 'ACCEPTED' || b.state === 'BOOKED');
  if (active.length > 0) {
    const names = active.map(b => b.personName ?? b.personId).join(', ');
    throw new Error(`Cancel ${names} before deleting this position — an accepted/booked person can't be removed by deleting the position.`);
  }

  const batch = writeBatch(db);
  bookingsSnap.docs.forEach((d, i) => {
    if (bookings[i].state !== 'CANCELLED') batch.delete(d.ref);
  });
  batch.delete(doc(db, ...base));
  await batch.commit();
}

// Booking operations — a Booking lives at
// positions/{posId}/bookings/{bookingId}, one doc per person per Position
// (never duplicated per call); its own `callIds` (a subset of the parent
// Position's) tracks which linked calls this person is actually committed
// to.
export async function createBooking(
  orgId: string,
  gigId: string,
  posId: string,
  booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const bookingsRef = collection(
    getFirestoreInstance(),
    'organizations',
    orgId,
    'gigs',
    gigId,
    'positions',
    posId,
    'bookings',
  );
  const bookingRef = doc(bookingsRef);
  const bookingId = bookingRef.id;

  await setDoc(bookingRef, {
    ...booking,
    id: bookingId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return bookingId;
}

export async function updateBooking(
  orgId: string,
  gigId: string,
  posId: string,
  bookingId: string,
  updates: Partial<Booking>,
): Promise<void> {
  const bookingRef = doc(
    getFirestoreInstance(),
    'organizations',
    orgId,
    'gigs',
    gigId,
    'positions',
    posId,
    'bookings',
    bookingId,
  );
  await updateDoc(bookingRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBooking(orgId: string, gigId: string, posId: string, bookingId: string): Promise<void> {
  const bookingRef = doc(
    getFirestoreInstance(),
    'organizations',
    orgId,
    'gigs',
    gigId,
    'positions',
    posId,
    'bookings',
    bookingId,
  );
  await deleteDoc(bookingRef);
}


// Backfills Gig.staffing for gigs created before it existed (manager+).
// Resolves with how many gigs were computed.
export const refreshGigStaffing = callable<{ orgId: string }, { refreshed: number }>('refreshGigStaffing');
